import moment from 'moment';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import './TrendTable.scss';

/** Beyond this many time buckets, only visible columns are mounted (horizontal virtual scroll). */
const COLUMN_VIRTUAL_THRESHOLD = 48;
const OVERSCAN_COLUMNS = 8;

function timeKey(t) {
    if (typeof t === 'number' && Number.isFinite(t)) return t;
    const v = moment(t).valueOf();
    return Number.isFinite(v) ? v : String(t);
}

function TrendTable(props) {
    const {
        headerList: propsHeaderList = [],
        parameterList = [],
        data,
        /** When set with enough columns, time columns are virtualized; should match fixed column width in CSS (e.g. 4.6rem). */
        virtualTimeColumnWidthPx,
    } = props;

    const [headerList, setHeaderList] = useState(['', ...propsHeaderList]);
    const [parameterListState, setParameterListState] = useState(parameterList);
    const [dataState, setDataState] = useState();

    const scrollRef = useRef(null);
    const paramColRef = useRef(null);
    const scrollRafRef = useRef(0);

    const [viewport, setViewport] = useState({ scrollLeft: 0, clientWidth: 0 });
    const [stickyColWidth, setStickyColWidth] = useState(0);

    const cw = typeof virtualTimeColumnWidthPx === 'number' && virtualTimeColumnWidthPx > 0
        ? virtualTimeColumnWidthPx
        : 0;
    const timeColCount = Array.isArray(propsHeaderList) ? propsHeaderList.length : 0;
    const shouldVirtualize = timeColCount >= COLUMN_VIRTUAL_THRESHOLD && cw > 0;

    useEffect(() => {
        if (props.headerList) setHeaderList(['', ...props.headerList]);
        setParameterListState([...props.parameterList]);
        setDataState(props.data ? { ...props.data } : undefined);
    }, [props]);

    const rowByTime = useMemo(() => {
        const m = new Map();
        const arr = dataState?.data;
        if (!Array.isArray(arr)) return m;
        arr.forEach((row) => {
            if (!row) return;
            m.set(timeKey(row.time), row);
        });
        return m;
    }, [dataState]);

    const findColumnRow = useCallback(
        (header) => rowByTime.get(timeKey(header)) || null,
        [rowByTime]
    );

    useLayoutEffect(() => {
        if (!shouldVirtualize || !paramColRef.current) return;
        setStickyColWidth(paramColRef.current.offsetWidth);
    }, [shouldVirtualize, parameterListState, timeColCount]);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth });
    }, [shouldVirtualize, timeColCount, dataState]);

    const onScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = requestAnimationFrame(() => {
            scrollRafRef.current = 0;
            setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth });
        });
    }, []);

    useEffect(
        () => () => {
            if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        },
        []
    );

    useEffect(() => {
        if (!shouldVirtualize) return;
        const el = scrollRef.current;
        if (!el) return;
        const sync = () => {
            setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth });
        };
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
        if (ro) ro.observe(el);
        window.addEventListener('resize', sync);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, [shouldVirtualize, timeColCount]);

    const virtualWindow = useMemo(() => {
        if (!shouldVirtualize) return null;
        const n = timeColCount;
        const { scrollLeft: left, clientWidth: vw } = viewport;
        const sw = stickyColWidth > 0 ? stickyColWidth : 130;

        const rawStart = Math.floor((left - sw) / cw);
        const rawEndExcl = Math.ceil((left + vw - sw) / cw);

        let startIdx = Math.max(0, rawStart - OVERSCAN_COLUMNS);
        let endIdxExcl = Math.min(n, rawEndExcl + OVERSCAN_COLUMNS);

        if (rawEndExcl <= 0) {
            endIdxExcl = Math.min(n, OVERSCAN_COLUMNS * 2);
        }
        if (endIdxExcl <= startIdx) {
            endIdxExcl = Math.min(n, startIdx + OVERSCAN_COLUMNS * 2);
        }

        const beforeW = startIdx * cw;
        const afterW = Math.max(0, (n - endIdxExcl) * cw);
        return {
            startIdx,
            endIdxExcl,
            beforeW,
            afterW,
            slice: propsHeaderList.slice(startIdx, endIdxExcl),
        };
    }, [shouldVirtualize, propsHeaderList, timeColCount, viewport, cw, stickyColWidth]);

    const renderTrendCell = (cell) => {
        if (cell != null && typeof cell === 'object' && !Array.isArray(cell) && 'value' in cell) {
            const text =
                cell.value == null || cell.value === ''
                    ? '- -'
                    : String(cell.value);
            const style = cell.textColor ? { color: cell.textColor } : undefined;
            return <span style={style}>{text}</span>;
        }
        if (cell == null || cell === '') return '- -';
        return String(cell);
    };

    const renderParameterColumn = (attachRef) => (
        <div className="column parameters" key="param-column" ref={attachRef}>
            <div className="parameter-heading param body parameters-width">Parameter</div>
            {parameterListState.map((param) => {
                const cellStyle = {
                    color:
                        param.label === 'HR'
                            ? '#67DF52'
                              : (param.label === 'SPO2' || param.label === 'PI' || param.label === 'PR')
                                ? '#3ADAE5'
                                : param.label === 'RR'
                                  ? '#FCC331'
                                  : '#FFFFFF',
                };
                return (
                    <div
                        className="parameter-cell  param parameters-width"
                        key={param.label}
                        style={cellStyle}
                    >
                        {param.label}
                    </div>
                );
            })}
        </div>
    );

    const renderTimeColumn = (header) => {
        const col = findColumnRow(header);
        return (
            <div className="column" key={`t-${String(header)}`}>
                <div className=" timestamp heading param">{moment(header).format('HH:mm')}</div>
                {parameterListState.map((param) => {
                    const raw = col ? col[param.name] : null;
                    return (
                        <div className="cell" key={param.name}>
                            {col ? renderTrendCell(raw) : '- -'}
                        </div>
                    );
                })}
            </div>
        );
    };

    const tableClassName = `trend-table${shouldVirtualize ? ' trend-table--virtual-horizontal' : ''}`;

    return (
        <div
            className={tableClassName}
            ref={scrollRef}
            onScroll={shouldVirtualize ? onScroll : undefined}
        >
            {renderParameterColumn(shouldVirtualize ? paramColRef : undefined)}
            {shouldVirtualize && virtualWindow ? (
                <>
                    <div
                        className="trend-table-virtual-spacer trend-table-virtual-spacer--before"
                        style={{ width: virtualWindow.beforeW, flexShrink: 0 }}
                        aria-hidden
                    />
                    {virtualWindow.slice.map((header) => renderTimeColumn(header))}
                    <div
                        className="trend-table-virtual-spacer trend-table-virtual-spacer--after"
                        style={{ width: virtualWindow.afterW, flexShrink: 0 }}
                        aria-hidden
                    />
                </>
            ) : (
                headerList.slice(1).map((header) => renderTimeColumn(header))
            )}
        </div>
    );
}

export default TrendTable;
